import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { shared } from "../styles.js";
import { pb } from "../api/client.js";
import { navigate } from "../nav.js";

/**
 * account-menu is the header's signed-in control: an avatar button opening a
 * dropdown with the account identity (which links to the user's own scope
 * page) and links to Profile, Security, My teams, and Sign out. Keyboard
 * contract: Enter/Space opens, Escape closes and refocuses the button,
 * ArrowUp/Down move between items, outside click closes.
 */
@customElement("account-menu")
export class AccountMenu extends LitElement {
  static override styles = [
    shared,
    css`
      :host {
        position: relative;
        display: inline-flex;
      }
      .avatar {
        width: 28px;
        height: 28px;
        border-radius: 8px;
        background: var(--accent);
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font: 700 11px/1 var(--sans);
        border: 0;
        cursor: pointer;
        padding: 0;
      }
      .avatar:focus-visible {
        outline: 2px solid var(--brand);
        outline-offset: 2px;
      }
      .menu {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        min-width: 200px;
        background: var(--surface);
        border: 1px solid var(--border);
        border-radius: var(--radius-lg, 10px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
        padding: 6px;
        z-index: 30;
      }
      .who {
        display: block;
        width: 100%;
        text-align: left;
        padding: 8px 10px;
        border-bottom: 1px solid var(--border);
        margin-bottom: 6px;
      }
      button.who {
        border: 0;
        border-bottom: 1px solid var(--border);
        background: none;
        cursor: pointer;
      }
      button.who:hover,
      button.who:focus-visible {
        background: var(--surface-3);
        outline: none;
      }
      .who .name {
        font: 600 13px/1.3 var(--sans);
        color: var(--text);
      }
      .who .email {
        font: 400 11.5px/1.3 var(--mono);
        color: var(--text-faint);
        word-break: break-all;
      }
      .item {
        display: block;
        width: 100%;
        text-align: left;
        font: 500 13px/1 var(--sans);
        color: var(--text-dim);
        padding: 8px 10px;
        border-radius: 7px;
        border: 0;
        background: none;
        cursor: pointer;
        text-decoration: none;
      }
      .item:hover,
      .item:focus-visible {
        background: var(--surface-3);
        color: var(--text);
        text-decoration: none;
        outline: none;
      }
      .sep {
        border-top: 1px solid var(--border);
        margin: 6px 0;
      }
    `,
  ];

  @state() private open = false;

  private onPointerDown = (e: PointerEvent) => {
    if (this.open && !e.composedPath().includes(this)) this.open = false;
  };

  private unsubAuth?: () => void;

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener("pointerdown", this.onPointerDown);
    // re-render on auth record changes (e.g. display-name update + authRefresh)
    this.unsubAuth = pb.authStore.onChange(() => this.requestUpdate());
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("pointerdown", this.onPointerDown);
    this.unsubAuth?.();
  }

  private items(): HTMLElement[] {
    return Array.from(this.renderRoot.querySelectorAll<HTMLElement>('[role="menuitem"]'));
  }

  private onMenuKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      this.open = false;
      (this.renderRoot.querySelector(".avatar") as HTMLElement | null)?.focus();
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const items = this.items();
      const idx = items.indexOf(this.shadowRoot?.activeElement as HTMLElement);
      const delta = e.key === "ArrowDown" ? 1 : -1;
      const next = items[(idx + delta + items.length) % items.length];
      next?.focus();
    }
  };

  private toggle() {
    this.open = !this.open;
    if (this.open) {
      void this.updateComplete.then(() => this.items()[0]?.focus());
    }
  }

  private go(path: string) {
    this.open = false;
    navigate(path);
  }

  private signOut() {
    this.open = false;
    pb.authStore.clear();
    navigate("/");
  }

  override render() {
    const record = pb.authStore.record;
    const email = (record?.email as string | undefined) ?? "";
    const name = (record?.name as string | undefined) || "";
    const handle = (record?.handle as string | undefined) || "";
    return html`
      <button
        class="avatar"
        aria-haspopup="menu"
        aria-expanded=${this.open ? "true" : "false"}
        aria-label=${`Account menu for ${name || handle || email}`}
        title=${email}
        @click=${this.toggle}
      >
        ${initials(name || handle || email)}
      </button>
      ${this.open
        ? html`
            <div class="menu" role="menu" @keydown=${this.onMenuKeydown}>
              ${handle
                ? html`<button class="who" role="menuitem" @click=${() => this.go(`/scopes/${handle}`)}>
                    <div class="name">${name || handle}</div>
                    <div class="email">${email}</div>
                  </button>`
                : html`<div class="who">
                    <div class="name">${name || handle}</div>
                    <div class="email">${email}</div>
                  </div>`}
              <button class="item" role="menuitem" @click=${() => this.go("/settings/profile")}>
                Profile
              </button>
              <button class="item" role="menuitem" @click=${() => this.go("/settings/security")}>
                Security
              </button>
              <button class="item" role="menuitem" @click=${() => this.go("/teams")}>
                My teams
              </button>
              <div class="sep" role="separator"></div>
              <button class="item" role="menuitem" @click=${this.signOut}>Sign out</button>
            </div>
          `
        : ""}
    `;
  }
}

/** initials derives up-to-two uppercase initials from a name, handle, or email local part. */
export function initials(source: string | undefined): string {
  const local = (source ?? "").split("@")[0] ?? "";
  const parts = local.split(/[\s._-]+/).filter(Boolean);
  const chars = parts.length >= 2 ? parts[0][0] + parts[1][0] : local.slice(0, 2);
  return (chars || "?").toUpperCase();
}

declare global {
  interface HTMLElementTagNameMap {
    "account-menu": AccountMenu;
  }
}
